import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  FilterModule,
  MultiSelectParserService,
  ShiftConfirmDeclineFilterComponent,
  TimelineFilterShiftsRegionSelectComponent,
  ToggleHighlightFilterComponent,
} from '@wilson/filter';
import {
  FeatureName,
  OperationStatus,
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  ShiftConfirmationStatus,
  TimelineActiveFilterRegionEnum,
} from '@wilson/interfaces';
import {
  RemoveTimelinesNonMatchingElements,
  ShiftTimelineFilterStateService,
} from '@wilson/shift-timeline/state';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { TimelineFilterHelperService } from '../timeline-filter-helper.service';
import { Store } from '@ngxs/store';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlusSquare } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'wilson-shift-filters-group',
  templateUrl: './shift-filters-group.component.html',
  styleUrls: ['./shift-filters-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NzCollapseModule,
    FeatureFlagsModule,
    AuthorizationModule,
    FilterModule,
    ShiftConfirmDeclineFilterComponent,
    ToggleHighlightFilterComponent,
    TimelineFilterShiftsRegionSelectComponent,
    NzSelectModule,
    NzTooltipModule,
    NzTreeSelectModule,
    FontAwesomeModule,
  ],
})
export class ShiftFiltersGroupComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly multiSelectParserService = inject(MultiSelectParserService);
  private readonly shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  protected readonly timelineFilterHelperService = inject(
    TimelineFilterHelperService,
  );

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  FeatureName = FeatureName;
  protected can = inject(AbilityServiceSignal).can;
  readonly faPlusSquare = faPlusSquare;
  readonly faChevronUp = faChevronUp;
  readonly faChevronDown = faChevronDown;

  ngOnInit(): void {
    this.updateStateWithQueryParams();
  }

  shiftCategoryChanged(shiftCategory: string[]): void {
    this.shiftTimelineFilterStateService.shiftCategoryIds = shiftCategory;
  }

  operationStatusChanged(operationStatus: OperationStatus[]): void {
    this.shiftTimelineFilterStateService.shiftOperationalStatus =
      operationStatus;
  }

  releaseStatusChanged(releaseStatus: PublicationStatus): void {
    this.shiftTimelineFilterStateService.releaseStatus = releaseStatus;
  }

  shiftProjectsChanged(projectIds: string[]): void {
    this.shiftTimelineFilterStateService.projectIds = projectIds;
  }

  confirmDeclineChanged(confirmedDeclined: ShiftConfirmationStatus[]): void {
    this.shiftTimelineFilterStateService.confirmedDeclined = confirmedDeclined;
  }

  orgUnitsChanged(orgIds: string[]): void {
    this.shiftTimelineFilterStateService.shiftOrgUnitIds = orgIds;
  }

  labelsChanged(labels: string[]): void {
    this.shiftTimelineFilterStateService.shiftLabels = labels;
  }

  professionChanged(professionIds: string[]): void {
    this.shiftTimelineFilterStateService.shiftProfessionIds = professionIds;
  }

  agreementChanged(agreementIds: string[]): void {
    this.shiftTimelineFilterStateService.agreementIds = agreementIds;
  }

  copyOrgUnitFromUser(): void {
    this.timelineFilterHelperService.copyField('orgUnit', 'shiftOrgUnit');
  }

  copyLabelFromUser(): void {
    this.timelineFilterHelperService.copyField('labels', 'shiftLabels');
  }

  copyProfessionFromUser(): void {
    this.timelineFilterHelperService.copyField('profession', 'shiftProfession');
  }

  shiftSearchChanged(searchString: string): void {
    this.shiftTimelineFilterStateService.shiftSearchString = searchString;
  }

  private updateStateWithQueryParams(): void {
    const filters = (({
      orgUnit,
      shiftCategory,
      shiftOperationalStatus,
      releaseStatus,
      priority,
      profession,
      labels,
      projects,
      confirmedDeclined,
    }) => ({
      orgUnit,
      shiftCategory,
      shiftOperationalStatus,
      releaseStatus,
      priority,
      profession,
      labels,
      projects,
      confirmedDeclined,
    }))(this.route.snapshot.queryParams);

    this.orgUnitsChanged(
      this.multiSelectParserService.unparse(filters.orgUnit),
    );
    this.shiftCategoryChanged(
      (filters.shiftCategory = this.multiSelectParserService.unparse(
        filters.shiftCategory,
      )),
    );
    this.operationStatusChanged(
      (filters.shiftOperationalStatus = this.multiSelectParserService.unparse(
        filters.shiftOperationalStatus,
      )),
    );
    this.releaseStatusChanged(filters.releaseStatus ?? null);
    this.shiftProjectsChanged(filters.projects || []);
    this.professionChanged(
      this.multiSelectParserService.unparse(filters.profession),
    );
    this.labelsChanged(this.multiSelectParserService.unparse(filters.labels));
    this.confirmDeclineChanged(
      this.multiSelectParserService.unparse(
        filters.confirmedDeclined,
      ) as ShiftConfirmationStatus[],
    );
  }

  protected toggleRenderUnmatchedElements(value: boolean): void {
    if (!value) {
      this.store.dispatch(new RemoveTimelinesNonMatchingElements());
    }

    this.shiftTimelineFilterStateService.setRenderUnmatchedElements(value);
  }

  protected setActiveToggleRegion(
    value: TimelineActiveFilterRegionEnum[],
  ): void {
    this.shiftTimelineFilterStateService.setFilterRegion(value);
  }
}
